import type { FC } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

import type { ResearchFilters } from "../routes";
import { CIRCUITS } from "../sources";
import type { ResearchSourceId } from "../types";

const ANY = "any";

const YEARS = Array.from({ length: 12 }, (_, i) =>
  String(new Date().getFullYear() - i)
);

type PickProps = {
  label: string;
  value: string | undefined;
  anyLabel: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string | undefined) => void;
};

const Pick: FC<PickProps> = ({ label, value, anyLabel, options, onChange }) => (
  <Select
    value={value ?? ANY}
    onValueChange={(v) => onChange(v === ANY ? undefined : v)}
  >
    <SelectTrigger
      size="sm"
      aria-label={label}
      className="h-7 w-auto gap-1 text-sm"
    >
      <span className="text-fg-3">{label}</span>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ANY}>{anyLabel}</SelectItem>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

type Props = {
  source: ResearchSourceId;
  filters: ResearchFilters;
  onChange: (filters: ResearchFilters) => void;
};

/** The filters a source takes: Case Law's forum, Unpublished's origin, year and form. */
export const SourceFilters: FC<Props> = ({ source, filters, onChange }) => {
  const set = (key: keyof ResearchFilters) => (value: string | undefined) =>
    onChange({ ...filters, [key]: value });
  if (source === "case_law")
    return (
      <Pick
        label="Forum"
        value={filters.circuit}
        anyLabel="Any Circuit"
        options={CIRCUITS.map((c) => ({
          value: c,
          label: c === "D.C." ? "D.C. Circuit" : `${c} Circuit`
        }))}
        onChange={set("circuit")}
      />
    );
  if (source === "unpublished")
    return (
      <>
        <Pick
          label="From"
          value={filters.origin}
          anyLabel="BIA and AAO"
          options={[
            { value: "eoir_pal", label: "Unpublished BIA" },
            { value: "uscis_aao", label: "AAO Non-Precedent" }
          ]}
          onChange={set("origin")}
        />
        <Pick
          label="Year"
          value={filters.year}
          anyLabel="Any Year"
          options={YEARS.map((y) => ({ value: y, label: y }))}
          onChange={set("year")}
        />
      </>
    );
  return null;
};
